import { useState, useMemo } from "react"
import { v4 as uuid } from "uuid"
import { useShallow } from "zustand/react/shallow"
import { cloneDeep } from "lodash"

// components
import DynamicField from "components/form-fields/DynamicField/DynamicField"
import MeasureRow from "../MeasureRow/MeasureRow"
import { PrimeButton } from "components-design-system"
import ResultsTableCreateModal from "../../../../components/ResultsTableCreateModal/ResultsTableCreateModal"
import ResultsTableDeleteModal from "../../../../components/ResultsTableDeleteModal/ResultsTableDeleteModal"
import RJTableCommentContainer from "../../../components/RJTableCommentContainer/RJTableCommentContainer"
import CellValidation from "../../../components/CellValidation/CellValidation"
import CharCounter from "components/CharCounter/CharCounter"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringCommentsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringCommentsStore"

// helpers
import { matchRegistryValues, moveLeftHelper, moveRightHelper } from "../../helpers/helpers"
import useResultsBaselineStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsBaselineStore"

const Measure = ({
  measure,
  measureIndex,
  arms,
  sasArr,
  index,
  schema,
  numOfMeasures,
  baselineData,
  setBaselineData,
  scrollXPosition,
  isSticky,
  rootSchema,
}) => {
  const [showAddRowModal, setShowAddRowModal] = useState(false)
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [showEudractAddCategoryModal, setShowEudractAddCategoryModal] = useState(false)
  const [showDeleteMeasureModal, setShowDeleteMeasureModal] = useState(false)

  const { showBcUnitsAnalyzed } = useResultsBaselineStore()

  const { tableView } = useResultsTableViewStore()

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const canAddCategoryHelper = (measure) => {
    let schemas = rootSchema.properties.ctg.properties.baseline_measure

    let bmSchema = schemas.anyOf.find((schema) => {
      if (schema.id === measure.baseline_measure_id) {
        return true
      } else if (
        schema.id === "eudract-specific-categorical" &&
        measure.baseline_measure_id === "ctg-specific-customized"
      ) {
        return true
      }
      return false
    })

    if (bmSchema?.addCategoryParamCond) {
      if (bmSchema.addCategoryParamCond.findIndex((mt) => mt === measure.parameter_type) !== -1) {
        return true
      }
      return false
    }
    return false
  }

  const canAddCategory = () => {
    if (readOnly !== true && schema.canAddCategories) {
      if (tableView === "ctg") {
        return canAddCategoryHelper(measure)
      } else if (tableView === "eudract") {
        let foundCtgBaseline = baselineData?.ctg?.baseline_measures.find((bm) => bm.id === measure.id)
        if (foundCtgBaseline) {
          return canAddCategoryHelper(foundCtgBaseline)
        }
      }

      return false
    }

    return false
  }

  const transformCatsToRows = (tempData) => {
    const rowIdMap = {}
    let numAnalyzed = {
      id: "num-analyzed",
      unique: true,
      title: "Number Analyzed",
      properties: {
        value: {
          type: "number",
        },
      },
    }

    Object.keys(tempData).forEach((registry) => {
      let foundIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
      if (foundIndex !== -1) {
        let newRows = []

        let bm = tempData[registry].baseline_measures[foundIndex]
        bm.rows.forEach((row) => {
          if (row.categories) {
            row.categories
              .filter((cat) => cat.id !== "num-analyzed")
              .forEach((category, index) => {
                let newRowId = `row-${uuid()}`
                if (rowIdMap[category.id]) newRowId = rowIdMap[category.id]
                else rowIdMap[category.id] = newRowId

                let rowTitle = `${category?.title}`
                if (row.title) rowTitle = `${row.title} : ${category?.title}`

                let newRow = {
                  id: newRowId,
                  title: rowTitle,
                  categories: [
                    {
                      id: `Category-${uuid()}`,
                      title: "",
                      values: category.values,
                    },
                  ],
                }

                if (registry === "ctg") {
                  if (index === 0) {
                    let numAnalyzedCategory = cloneDeep(row.categories[0])
                    newRow.categories = [numAnalyzedCategory, newRow.categories[0]]
                  } else {
                    const numAnalyzedCategory = {
                      id: `num-analyzed`,
                      title: "Number Analyzed",
                      values: baselineData[registry].arms.map((item) => {
                        return {
                          arm_id: item.id,
                          registry_arm_id: item.id,
                          value: item.baseline_analysis_population,
                        }
                      }),
                    }
                    newRow.categories = [numAnalyzedCategory, newRow.categories[0]]
                  }
                }
                newRows.push(newRow)
              })
          }
        })
        tempData[registry].baseline_measures[foundIndex].rows = newRows
      }
    })
  }

  const transformBaselineMeasure = (tempData, type) => {
    let foundBMIndex = baselineData["eudract"].baseline_measures.findIndex((item) => item.id === measure.id)
    if (foundBMIndex !== -1) {
      let updatedBM = { ...baselineData["eudract"].baseline_measures[foundBMIndex] }
      if (!updatedBM.baseline_measure_id.includes(type)) {
        updatedBM.baseline_measure_id = "eudract-specific-" + type
        if (type === "continuous") {
          updatedBM.parameter_type = measure.parameter_type
          updatedBM.dispersion_type = measure.dispersion_type
        } else if (type === "categorical") {
          delete updatedBM.parameter_type
          delete updatedBM.dispersion_type
        }
      }
      tempData.eudract.baseline_measures.splice(foundBMIndex, 1, updatedBM)
    }
  }

  const onChangeProperties = (e, key) => {
    let val = e
    if (e.id) val = e.text

    let tempData = { ...baselineData }

    Object.keys(tempData).forEach((registry) => {
      let foundIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
      if (foundIndex !== -1) {
        if (
          key === "title" &&
          (measure.baseline_measure_id === "eudract-specific-categorical" ||
            measure.baseline_measure_id === "eudract-specific-continuous")
        ) {
          if (tempData[registry].baseline_measures[foundIndex] !== "ctg-specific-customized" && registry === "ctg") {
            return
          }
        }

        if (key === "parameter_type") {
          if (
            tempData[registry].baseline_measures[foundIndex][key] !== "Count of Participants" &&
            val === "Count of Participants" &&
            tableView === "ctg"
          ) {
            // If ctg continuous and now categorical
            transformBaselineMeasure(tempData, "categorical")
          }
          if (val !== "Count of Participants" && tableView === "ctg") {
            // If ctg is categorical and now continuous
            transformBaselineMeasure(tempData, "continuous")
          }

          if (
            tempData[registry].baseline_measures[foundIndex][key] !== "Count of Participants" &&
            tempData[registry].baseline_measures[foundIndex][key] !== "Number" &&
            tempData[registry].baseline_measures[foundIndex][key] !== "Count of Units" &&
            (val === "Count of Participants" || val === "Number" || val === "Count of Units")
          ) {
            tempData[registry].baseline_measures[foundIndex].dispersion_type = "Not Applicable"
            // handleNAValues(tempData, registry, "Not Applicable")
          }

          if (
            (tempData[registry].baseline_measures[foundIndex][key] === "Count of Participants" ||
              tempData[registry].baseline_measures[foundIndex][key] === "Number" ||
              tempData[registry].baseline_measures[foundIndex][key] === "Count of Units") &&
            val !== "Count of Participants" &&
            val !== "Number" &&
            val !== "Count of Units"
          ) {
            tempData[registry].baseline_measures[foundIndex].dispersion_type = "Standard Deviation"
            // handleNAValues(tempData, registry, "Standard Deviation")
          }
          if (e === "Count of Units" && registry === "eudract") val = "Count of Participants"
          if (e === "Count of Units" && registry === "ctg")
            tempData[registry].baseline_measures[foundIndex].selected_unit_of_analysis = "U"
          else if (e === "Count of Participants" && registry === "ctg")
            tempData[registry].baseline_measures[foundIndex].selected_unit_of_analysis = "Participants"
        }

        if (matchRegistryValues?.[tableView]?.[registry]?.[key]?.[val]) {
          tempData[registry].baseline_measures[foundIndex][key] = matchRegistryValues[tableView][registry][key][val]
        } else {
          tempData[registry].baseline_measures[foundIndex][key] = val
        }
      }
    })
    // transform cats to rows
    if (
      key === "parameter_type" &&
      canAddCategory() === true &&
      canAddCategoryHelper({
        ...measure,
        parameter_type: val,
      }) === false
    ) {
      transformCatsToRows(tempData)
    }

    setBaselineData(tempData)
  }

  const handleUp = () => {
    let tempData = { ...baselineData }
    Object.keys(tempData).forEach((registry) => {
      let foundIndex = tempData[registry].baseline_measures.findIndex((item) => item.id === measure.id)
      if (foundIndex !== -1 && foundIndex !== 0) {
        moveLeftHelper(tempData, registry, "baseline_measures", foundIndex)
      }
    })

    setBaselineData(tempData)
  }

  const handleDown = () => {
    let tempData = { ...baselineData }
    Object.keys(tempData).forEach((registry) => {
      let foundIndex = tempData[registry].baseline_measures.findIndex((item) => item.id === measure.id)
      if (foundIndex !== -1 && foundIndex !== tempData[registry].baseline_measures.length - 1) {
        moveRightHelper(tempData, registry, "baseline_measures", foundIndex)
      }
    })

    setBaselineData(tempData)
  }

  const addRow = (applyAll) => {
    let tempData = { ...baselineData }
    let rowId = `row-${uuid()}`

    let categoryIds = []

    for (let i = 0; i < 15; i++) {
      let cid = `Category-${uuid()}`
      categoryIds.push(cid)
    }

    Object.keys(tempData).forEach((registry) => {
      if (applyAll || registry === tableView) {
        let foundIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)
        let catIndex = 0

        if (foundIndex !== -1) {
          let bm = tempData[registry].baseline_measures[foundIndex]

          let rows = [...bm.rows]
          let newRow = {
            id: rowId,
            title: "",
            categories: rows[0].categories.map((category) => {
              let values = []
              if (category.id === "num-analyzed") {
                values = baselineData[registry].arms.map((item) => {
                  return {
                    arm_id: item.id,
                    registry_arm_id: item.id,
                    value: item.baseline_analysis_population,
                  }
                })
              } else {
                values = category.values.map((valueObj) => {
                  let newVal = { arm_id: valueObj.arm_id }
                  Object.keys(valueObj).forEach((key) => {
                    if (key !== "arm_id") {
                      newVal[key] = null
                    }
                  })
                  return newVal
                })
              }

              if (category.id !== "num-analyzed") {
                catIndex++
              }

              return {
                id: category.id === "num-analyzed" ? "num-analyzed" : categoryIds[catIndex],
                title: category.title,
                values,
              }
            }),
          }

          rows.push(newRow)
          bm.rows = rows
        }
      }
    })
    setBaselineData(tempData)
  }

  const addCategory = (applyAll) => {
    let tempData = { ...baselineData }
    let cid = `Category-${uuid()}`
    let rid = `row-${uuid()}`

    Object.keys(tempData).forEach((registry) => {
      if (applyAll || registry === tableView) {
        let foundIndex = tempData[registry].baseline_measures.findIndex((bm) => bm.id === measure.id)

        if (foundIndex !== -1) {
          let bm = tempData[registry].baseline_measures[foundIndex]
          let rows = [...bm.rows]

          if (rows.length === 0) {
            rows.push({
              id: rid,
              title: "",
              categories: [],
            })
          }
          rows.forEach((row) => {
            let values = tempData[registry].arms.map((arm) => {
              return {
                arm_id: arm.id,
                value: null,
              }
            })

            if (registry === "eudract" && tempData[registry].subject_analysis_sets) {
              let sasValues = tempData[registry].subject_analysis_sets.map((arm) => {
                return {
                  arm_id: arm.id,
                  value: null,
                }
              })
              values = [...values, ...sasValues]
            }

            let newCat = {
              id: cid,
              title: "",
              values,
            }
            row.categories.push({ ...newCat })
          })
          bm.rows = rows
        }
      }
    })
    setBaselineData(tempData)
  }

  const handleAddEudractCategory = (applyAll, options) => {
    let shouldCreateRow = options.createRowCtg
    let shouldCreateCategory = options.createCategoryCtg

    if (applyAll !== true) {
      if (measure.baseline_measure_id === "eudract-gender-categorical") {
        addCategory(applyAll)
        return
      } else if (
        measure.baseline_measure_id === "eudract-age-categorical" ||
        measure.baseline_measure_id === "eudract-specific-categorical"
      ) {
        if (measure.rows.length === 1 && measure.rows[0]?.categories?.length > 1) {
          addCategory(applyAll)
          return
        }
      }
    }

    if (shouldCreateRow) {
      addRow(applyAll)
    } else if (shouldCreateCategory) {
      addCategory(applyAll)
    }
  }

  const handleDeleteMeasure = (applyAll) => {
    let tempData = { ...baselineData }
    Object.keys(tempData).forEach((registry) => {
      if (applyAll || registry === tableView) {
        let foundIndex = tempData[registry].baseline_measures.findIndex((item) => item.id === measure.id)
        let bmMeasures = [...tempData[registry].baseline_measures]
        if (foundIndex !== -1) {
          bmMeasures.splice(foundIndex, 1)
          tempData[registry].baseline_measures = bmMeasures
        }
      }
    })

    setBaselineData(tempData)
  }

  const renderHeader = () => {
    const getColSpan = () => {
      if (arms?.length > 1 || sasArr?.length > 1) return 4
      return arms?.length + 3
    }

    return (
      <tr className="divider-row">
        <td colSpan={getColSpan()}>
          {readOnly !== true && (
            <div className="measure-actions">
              <div className="bc-reorder-actions">
                {index !== 0 && (
                  <i
                    className="fal fa-arrow-up arrow-icon"
                    style={{ marginRight: 16 }}
                    onClick={() => handleUp()}
                  />
                )}
                {index !== numOfMeasures - 1 && (
                  <i
                    className="fal fa-arrow-down arrow-icon"
                    onClick={() => handleDown()}
                  />
                )}
              </div>

              <i
                onClick={() => setShowDeleteMeasureModal(true)}
                className="fal fa-trash-alt trash-icon"
              />
            </div>
          )}
        </td>
      </tr>
    )
  }

  const getColSpan = () => {
    if (arms?.length > 1 || sasArr?.length > 1) return 2
    return 2
  }

  const renderProperties = () => {
    let rows = []

    if (schema?.properties) {
      Object.keys(schema?.properties).forEach((key) => {
        let fieldSchema = schema.properties[key]

        if (key === "parameter_type" && tableView === "ctg" && showBcUnitsAnalyzed) {
          let options = fieldSchema.enum

          if (fieldSchema.enum) {
            if (schema.addCountOfUnits !== false) {
              options = [...fieldSchema.enum, "Count of Units"]
            }
          } else {
            options = ["Count of Participants", "Count of Units"]
          }
          fieldSchema = {
            ...fieldSchema,
            // readOnly: false,
            enum: options,
          }
        }

        rows.push(
          <tr key={`${measure?.id}-${tableView}-${key}-props-${index}`}>
            <PropertyHeader
              measure={measure}
              fieldKey={key}
              fieldSchema={fieldSchema}
            />
            <td
              className="text-left"
              colSpan={getColSpan()}
            >
              <DynamicField
                type={fieldSchema.type}
                fieldProps={{
                  options: fieldSchema.enum,
                  readOnly,
                  disabled:
                    fieldSchema.readOnly ||
                    (key === "dispersion_type" &&
                      (measure.parameter_type === "Count of Participants" ||
                        measure.parameter_type === "Number" ||
                        measure.parameter_type === "Count of Units")),
                  onChange: (e) => onChangeProperties(e, key),
                  value: measure[key],
                }}
              />
              {fieldSchema?.charCountEnabled && formView?.id !== "global" && !readOnly && (
                <div className="char-counter-container">
                  <CharCounter
                    value={measure[key] || ""}
                    maxChars={fieldSchema.maxChars}
                  />
                </div>
              )}
              <CellValidation
                table="baseline"
                errorKey={`results_baseline_characteristics_baseline_characteristics_table_${tableView}_baseline_measures_${
                  measureIndex + 1
                }_${key}`}
              />
            </td>
          </tr>
        )
      })
    }

    if (
      tableView === "ctg" &&
      showBcUnitsAnalyzed &&
      schema.selectedUnitOfAnalysis?.enable === true &&
      schema?.selectedUnitOfAnalysis?.paramTypeCond?.find((val) => val === measure?.parameter_type)
    ) {
      const analysisSchema = schema.selectedUnitOfAnalysis.properties
      const key = "selected_unit_of_analysis"
      let unitOfAnalysisLabel = ""

      let items = analysisSchema.enum.map((item) => {
        if (item.label === "Other Units" && baselineData.ctg.type_units_analyzed) {
          unitOfAnalysisLabel = `Other Units (${baselineData.ctg.type_units_analyzed})`
          return { ...item, label: unitOfAnalysisLabel }
        }
        return item
      })

      let value = measure[key] || ""

      if (value === "U" && readOnly) {
        value = unitOfAnalysisLabel || "Other Units"
      }

      rows.push(
        <tr key={`${measure?.id}-${tableView}-${key}-props-${index}`}>
          <PropertyHeader
            measure={measure}
            fieldKey={key}
            fieldSchema={analysisSchema}
          />
          <td
            className="text-left"
            colSpan={getColSpan()}
          >
            <DynamicField
              type={analysisSchema.type}
              fieldProps={{
                options: items,
                disabled: analysisSchema.readOnly,
                readOnly,
                onChange: (e) => onChangeProperties(e, key),
                value,
              }}
            />
            <CellValidation
              table="baseline"
              errorKey={`results_baseline_characteristics_baseline_characteristics_table_${tableView}_baseline_measures_${
                measureIndex + 1
              }_${key}`}
            />
          </td>
        </tr>
      )
    }
    return rows
  }

  const actionsRegistrySpecific = (key) => {
    let isRegistrySpecific = false
    Object.keys(baselineData).forEach((registry) => {
      let foundIndex = baselineData[registry].baseline_measures.findIndex((item) => item.id === measure.id)
      if (foundIndex !== -1) {
        let om = baselineData[registry].baseline_measures[foundIndex]
        let bmSchemas = rootSchema.properties[registry].properties.baseline_measure.anyOf
        let foundSchemaIndex = bmSchemas.findIndex((bmSchema) => bmSchema.id === om.baseline_measure_id)

        if (foundSchemaIndex !== -1) {
          let bmSchema = bmSchemas[foundSchemaIndex]
          if (bmSchema[key] === false) isRegistrySpecific = true
        }
      }
    })
    return isRegistrySpecific
  }

  const renderRows = () => {
    return measure?.rows?.map((row, i) => {
      return (
        <MeasureRow
          key={`${tableView}-${measure.id}-${row.id}-cat-${i}`}
          arms={arms}
          rows={measure.rows}
          rowIndex={i}
          schema={schema.rows}
          bmSchema={schema}
          measure={measure}
          measureIndex={measureIndex}
          row={row}
          baselineData={baselineData}
          setBaselineData={setBaselineData}
          scrollXPosition={scrollXPosition}
          canAddCategory={canAddCategory}
          isSticky={isSticky}
          actionsRegistrySpecific={actionsRegistrySpecific}
        />
      )
    })
  }

  const isRegionOfEnrollmentMeasure = () => {
    let foundCtgBaseline = baselineData?.ctg?.baseline_measures.find((bm) => bm.id === measure.id)
    if (foundCtgBaseline?.baseline_measure_id === "ctg-region-categorical") {
      return true
    }
    return false
  }

  const renderActions = () => {
    const getAddRowButtonTitle = () => {
      if (isRegionOfEnrollmentMeasure()) return "Add Region"
      return "Add Row"
    }

    if (measure.baseline_measure_id === "not-collected") {
      return
    }

    const getColSpan = () => {
      if (arms?.length > 1 || sasArr?.length > 1) return 4
      return arms?.length + 3
    }

    return (
      <tr className="disabled-row">
        <td colSpan={getColSpan()}>
          <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
            {readOnly !== true && schema.canAddRows && (
              <PrimeButton
                type="add"
                size="small"
                style={{ marginRight: 12 }}
                onClick={() => setShowAddRowModal(true)}
              >
                {getAddRowButtonTitle()}
              </PrimeButton>
            )}
            {canAddCategory() && (
              <PrimeButton
                type="add"
                size="small"
                onClick={() => setShowAddCategoryModal(true)}
              >
                Add Category
              </PrimeButton>
            )}
          </div>
          <CellValidation
            table="baseline"
            errorKey={`results_baseline_characteristics_baseline_characteristics_table_${tableView}_baseline_measures_${
              measureIndex + 1
            }`}
          />
        </td>
      </tr>
    )
  }

  return (
    <>
      {renderHeader()}
      {renderProperties()}
      {renderRows()}
      {renderActions(measure.parameter_type)}

      <ResultsTableDeleteModal
        id={`bc-delete-baseline-measure-${measure.id}`}
        header="Delete Baseline Measure"
        onSubmit={handleDeleteMeasure}
        showModal={showDeleteMeasureModal}
        registrySpecific={measure?.baseline_measure_id === "not-collected"}
        setShowModal={() => setShowDeleteMeasureModal(false)}
      />

      {/* add row */}
      <ResultsTableCreateModal
        id={`bc-${measure.id}-add-row`}
        header="Add Row"
        onSubmit={addRow}
        showModal={showAddRowModal}
        registrySpecific={actionsRegistrySpecific("canAddRows")}
        setShowModal={() => setShowAddRowModal(false)}
      />

      {/* add category */}
      <ResultsTableCreateModal
        id={`bc-${measure.id}-add-category`}
        header="Add Category"
        onSubmit={addCategory}
        showModal={showAddCategoryModal}
        registrySpecific={actionsRegistrySpecific("canAddCategories")}
        setShowModal={() => setShowAddCategoryModal(false)}
      />
      {/* add eudract category */}
      <ResultsTableCreateModal
        id={`bc-${measure.id}-eudract-add-category`}
        header="Add Category"
        onSubmit={handleAddEudractCategory}
        showModal={showEudractAddCategoryModal}
        addEudractCategory={true}
        setShowModal={() => setShowEudractAddCategoryModal(false)}
      />
    </>
  )
}

export default Measure

const PropertyHeader = ({ measure, fieldSchema, fieldKey }) => {
  const [openCommentsForm, setOpenCommentsForm] = useState(false)
  const [showActions, setShowActions] = useState(false)

  const { commentsMap } = useAuthoringCommentsStore(
    useShallow((state) => ({
      commentsMap: state.commentsMap,
    }))
  )

  let jpath = `BaselineReportingGroup.bc-measure-prop-${measure.id}-${fieldKey}`

  const commentStyle = {
    position: "absolute",
    right: 8,
    top: 8,
  }

  return (
    <td
      className="header-column"
      colSpan={2}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{ position: "relative" }}
    >
      {fieldSchema.title}
      <RJTableCommentContainer
        label={fieldSchema.title}
        jpath={jpath}
        openCommentsForm={openCommentsForm}
        setOpenCommentsForm={setOpenCommentsForm}
        setShowMenu={setShowActions}
        showMenu={showActions || commentsMap[jpath] || openCommentsForm}
        style={commentStyle}
        modalStyle={{ top: 24 }}
      />
    </td>
  )
}
